import React from 'react';
import { Building2 } from 'lucide-react';
import { formatCurrency, parseCurrency, formatZipCode } from '../../utils/formatting';
import { AddressAutocomplete, type ResolvedAddress } from '../AddressAutocomplete';

interface Step3Props {
  formData: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

export function Step3PropertyDetails({ formData, onChange }: Step3Props) {
  // The parent owns form state via a single onChange handler keyed by input
  // name, so setting a field means synthesising the event it expects.
  const emit = (name: string, value: string) =>
    onChange({ target: { name, value } } as React.ChangeEvent<HTMLInputElement>);

  // Fill the dependent fields from a confirmed Google address. These stay
  // editable — Google occasionally disagrees with a county on which
  // municipality a parcel sits in, and the partner may know better.
  const fillFromAddress = (a: ResolvedAddress) => {
    if (a.city) emit('propertyCity', a.city);
    if (a.state) emit('propertyState', a.state);
    if (a.zip) emit('propertyZip', a.zip);
    if (a.county) emit('propertyCounty', a.county);
  };

  // Calculate date range: 1900 to 2100 (wide range to allow any historical or future dates)
  const today = new Date();
  const minDate = new Date(1900, 0, 1);
  const maxDate = new Date(2100, 11, 31);

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow user to type, format on blur
    onChange(e);
  };

  const handleCurrencyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const formatted = formatCurrency(parseCurrency(value));
      onChange({
        ...e,
        target: {
          ...e.target,
          name: e.target.name,
          value: formatted
        }
      } as any);
    }
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatZipCode(e.target.value);
    onChange({
      ...e,
      target: {
        ...e.target,
        name: e.target.name,
        value: formatted
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
        <Building2 className="w-5 h-5 text-tdb-accent" />
        <span>Property Details</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Property Address.
            Autocompleted so the stored value is the canonical postal form.
            A free-typed address cannot be matched against county parcel records
            reliably, and an unmatched address means the deal gets no comps. */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Property Address - Street *
          </label>
          <AddressAutocomplete
            name="propertyStreet"
            value={formData.propertyStreet}
            required
            placeholder="Start typing the address and pick from the list"
            onChange={(v) => emit('propertyStreet', v)}
            onResolved={fillFromAddress}
            className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
          <p className="mt-1 text-xs text-gray-500">
            Selecting a suggestion fills in city, state, ZIP and county
            automatically.
          </p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Property Address - Line 2
          </label>
          <input
            type="text"
            name="propertyLine2"
            value={formData.propertyLine2}
            onChange={onChange}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            City *
          </label>
          <input
            type="text"
            name="propertyCity"
            value={formData.propertyCity}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            State *
          </label>
          <input
            type="text"
            name="propertyState"
            value={formData.propertyState}
            onChange={onChange}
            required
            maxLength={2}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="FL"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Zip Code *
          </label>
          <input
            type="text"
            name="propertyZip"
            value={formData.propertyZip}
            onChange={handleZipChange}
            required
            maxLength={10}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="12345 or A1A 1A1"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Property County *
          </label>
          <p className="text-xs text-gray-500 mb-2">Which county does your property reside in</p>
          <input
            type="text"
            name="propertyCounty"
            value={formData.propertyCounty}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        {/* Tax Information */}
        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Tax Sale Date *
          </label>
          <input
            type="date"
            name="taxSaleDate"
            value={formData.taxSaleDate}
            onChange={onChange}
            required
            min={formatDateForInput(minDate)}
            max={formatDateForInput(maxDate)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Tax Sale Amount *
          </label>
          <input
            type="text"
            name="taxSaleAmount"
            value={formData.taxSaleAmount}
            onChange={handleCurrencyChange}
            onBlur={handleCurrencyBlur}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="$5,000"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            County Tax Rules Breakdown
          </label>
          <textarea
            name="countyTaxRules"
            value={formData.countyTaxRules}
            onChange={onChange}
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all resize-none"
            placeholder="Describe any specific county tax rules or deadlines..."
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Public Property Tax Record Link *
          </label>
          <input
            type="url"
            name="publicTaxRecordLink"
            value={formData.publicTaxRecordLink}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="https://..."
          />
        </div>

        {/* Property Specs */}
        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Bedrooms *
          </label>
          <select
            name="bedrooms"
            value={formData.bedrooms}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5+">5+</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Bathrooms *
          </label>
          <select
            name="bathrooms"
            value={formData.bathrooms}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5+">5+</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Living Space (Sq. Ft.) *
          </label>
          <input
            type="number"
            name="sqft"
            value={formData.sqft}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="1,500"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Length of Ownership in years *
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            name="ownershipLength"
            value={formData.ownershipLength}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="e.g., 5"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Do you have active property insurance? *
          </label>
          <select
            name="hasInsurance"
            value={formData.hasInsurance}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Property Condition (Scope of Work) *
          </label>
          <textarea
            name="propertyCondition"
            value={formData.propertyCondition}
            onChange={onChange}
            required
            rows={4}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all resize-none"
            placeholder="Describe the current condition and needed repairs..."
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Total Estimate Cost of Repairs (Rehab) *
          </label>
          <input
            type="text"
            name="repairCost"
            value={formData.repairCost}
            onChange={handleCurrencyChange}
            onBlur={handleCurrencyBlur}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="$25,000"
          />
        </div>

        {/* Mortgage Section */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Do you have an active mortgage? *
          </label>
          <select
            name="hasMortgage"
            value={formData.hasMortgage}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        {/* Conditional Mortgage Fields */}
        {formData.hasMortgage === 'yes' && (
          <>
            <div className="md:col-span-2 bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-amber-900 mb-4">Mortgage Information Required</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-tdb-primary mb-2">
                    Bank or Credit Institution *
                  </label>
                  <input
                    type="text"
                    name="mortgageBank"
                    value={formData.mortgageBank}
                    onChange={onChange}
                    required={formData.hasMortgage === 'yes'}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-tdb-primary mb-2">
                      Mortgage Balance *
                    </label>
                    <input
                      type="text"
                      name="mortgageBalance"
                      value={formData.mortgageBalance}
                      onChange={handleCurrencyChange}
                      onBlur={handleCurrencyBlur}
                      required={formData.hasMortgage === 'yes'}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                      placeholder="$150,000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-tdb-primary mb-2">
                      Monthly Payment *
                    </label>
                    <input
                      type="text"
                      name="mortgagePayment"
                      value={formData.mortgagePayment}
                      onChange={handleCurrencyChange}
                      onBlur={handleCurrencyBlur}
                      required={formData.hasMortgage === 'yes'}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                      placeholder="$1,200"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
